/**
 * Browser notifications for inbound calls.
 *
 * Fires only when the tab is hidden (document.hidden). Click brings focus
 * back and opens the widget. Permission is opt-in via the banner.
 */

import { emit } from './events';
import { savePreferences } from './devices';
import type { Brand, ClientCallId, IncomingTransferInfo } from './types';

const active = new Map<ClientCallId, Notification>();

export function permissionState(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export function isGranted(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

export async function requestPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') {
    void savePreferences({ notificationsGranted: true });
    return 'granted';
  }
  if (Notification.permission === 'denied') return 'denied';

  const result = await Notification.requestPermission();
  void savePreferences({ notificationsGranted: result === 'granted' });

  emit({
    type: result === 'granted' ? 'notification_permission_granted' : 'notification_permission_denied',
    source: 'client',
  });
  return result;
}

interface ShowIncomingParams {
  clientCallId: ClientCallId;
  callerName: string;
  brand: Brand | string;
  /** Doel-leg van een doorverbinding: toont wie doorverbindt i.p.v. een
   *  anoniem "Inkomend gesprek". */
  transfer?: IncomingTransferInfo | null;
  onClick: () => void;
}

export function showIncoming(params: ShowIncomingParams): void {
  if (!isGranted()) return;
  // Only surface when the tab is hidden — don't spam when user is already looking.
  if (!document.hidden) return;

  const title = params.transfer
    ? params.transfer.mode === 'warm'
      ? `${params.transfer.from ?? 'Collega'} wil overleggen (doorverbinden) — ${params.brand}`
      : `Doorverbonden door ${params.transfer.from ?? 'collega'} — ${params.brand}`
    : `Inkomend gesprek — ${params.brand}`;

  try {
    const n = new Notification(title, {
      body: params.callerName,
      tag: params.clientCallId,
      requireInteraction: true,
      // Stil: wij spelen zelf al een beltoon op het gekozen apparaat. Het
      // OS-notificatiegeluid ging via het systeem-alertapparaat (vaak de
      // laptopspeakers) en klonk als "rinkelt óók op de computer".
      silent: true,
    });
    n.onclick = () => {
      window.focus();
      params.onClick();
      emit({
        type: 'notification_clicked',
        source: 'client',
        clientCallId: params.clientCallId,
      });
      n.close();
      active.delete(params.clientCallId);
    };
    active.set(params.clientCallId, n);
    emit({
      type: 'notification_shown',
      source: 'client',
      clientCallId: params.clientCallId,
    });
  } catch (err) {
    console.warn('[telephony/notifications] show failed', err);
  }
}

export function closeFor(clientCallId: ClientCallId): void {
  const n = active.get(clientCallId);
  if (!n) return;
  try {
    n.close();
  } catch {
    /* ignore */
  }
  active.delete(clientCallId);
}

export function closeAll(): void {
  active.forEach((n) => {
    try {
      n.close();
    } catch {
      /* ignore */
    }
  });
  active.clear();
}
